import { omit } from "es-toolkit/object";
import type { CompareBodyParams } from "./ComparativeAnalysisPage.types";
import type { GroupType } from "./components/ComparativeAnalysisForm/ComparativeAnalysisForm.types";
import { ALLOWED_METRIC_TYPES } from "./components/ComparativeAnalysisMetrics/ComparativeAnalysisMetrics.constants";
import {
  calcDelta,
  isMetricDeltaInThresholdRange,
} from "./components/ComparativeAnalysisMetrics/ComparativeAnalysisMetrics.utils";

const OMITTED_KEYS = ["task", "pipelineId"];

type GroupsType = {
  group1: GroupType;
  group2: GroupType;
};

export const checkGroupsForMissingFields = ({ group1, group2 }: GroupsType) =>
  Object.values(omit(group1, OMITTED_KEYS)).some((value) => !value) ||
  Object.values(omit(group2, OMITTED_KEYS)).some((value) => !value);

export const checkIfGroupsAreEmpty = ({ group1, group2 }: GroupsType) =>
  Object.values(group1).every((value) => !value) &&
  Object.values(group2).every((value) => !value);

export const checkGroupsFieldsIdentical = ({ group1, group2 }: GroupsType) => {
  const requiredFieldsIdentical =
    group1.env === group2.env &&
    group1.pipeline === group2.pipeline &&
    group1.pit === group2.pit;

  if (group1.task || group2.task) {
    return requiredFieldsIdentical && group1.task === group2.task;
  }

  return requiredFieldsIdentical;
};

export const getComparativeScoresFromMetrics = (metrics, similarityThreshold) =>
  metrics?.reduce((acc, item) => {
    const type = item.metric_group_type;
    const delta = calcDelta(item);

    const metricDeltaInThresholdRange = isMetricDeltaInThresholdRange(
      delta,
      similarityThreshold,
      item.metric_group_type
    );

    const allowed =
      ALLOWED_METRIC_TYPES.includes(item.metric_type) && item.metric_id;

    if (!allowed || delta === undefined) {
      return acc;
    }

    // initialize if needed
    if (!acc[type]) {
      acc[type] = {
        similar: 0,
        total: 0,
      };
    }

    if (metricDeltaInThresholdRange) {
      acc[type].similar++;
    }

    acc[type].total++;

    return acc;
  }, {});

export const validateBodyParams = (params: CompareBodyParams) => {
  for (const p in params) {
    if (!params[p as keyof CompareBodyParams]) return false;
  }

  return true;
};
